import { DeleteDialogContent } from '@components/dialogs/delete.dialog';
import { Dialog, DialogTrigger } from '@components/ui/dialog';
import {
  SettingHeader,
  SettingsContainer,
  SettingsContent
} from '@components/ui/settings';
import useProcess from '@hooks/useProcess';
import useStores from '@hooks/useStore';
import { Loader2 } from 'lucide-react';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoaderBox } from '@/components/ui/loader';
import { EnvVarsCard } from './EnvVarsCard';
import { WorkflowListCard } from './WorkflowListCard';
import { WorkspaceSettingsCard } from './WorkspaceSettingsCard';

const AdminSetting = () => {
  const process = useProcess();
  const { processStore, workflowStore, actionStore, transitionStore } =
    useStores();

  const [newWorkflowName, setNewWorkflowName] = useState<string>('');
  const [creatingWorkflow, setCreatingWorkflow] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [processName, setProcessName] = useState(process?.name ?? '');
  const [emptyNameError, setEmptyNameError] = useState(false);

  useEffect(() => {
    setProcessName(process?.name ?? '');
  }, [process]);

  if (!process || process.isSmallProcess) {
    return (
      <LoaderBox>
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </LoaderBox>
    );
  }

  const onIconBlur = async () => {
    await processStore.adminUpdateProcess(process.id, { icon: process.icon });
  };

  const onImageBlur = async () => {
    await processStore.adminUpdateProcess(process.id, { image: process.image });
  };

  const onNameBlur = async () => {
    if (!processName.trim()) {
      setEmptyNameError(true);
      return;
    }
    if (emptyNameError) {
      setEmptyNameError(false);
    }
    await processStore.updateProcess(process.id, { name: processName.trim() });
    process.setName(processName.trim());
  };

  const canDelete = process.permission.can_delete;

  async function deleteProcess() {
    if (!process?.id) {
      return;
    }
    const response = await processStore.deleteProcess(process.id);
    if (response) {
      window.location.assign('/');
    }
  }

  const onCreateWorkflow = async () => {
    if (!newWorkflowName.trim()) {
      return;
    }
    setCreatingWorkflow(true);
    const result = await workflowStore.adminCreateWorkflow({
      name: newWorkflowName.trim(),
      processId: process.id
    });
    setCreatingWorkflow(false);
    if (result) {
      await processStore.refetchProcess(process.id);
      setNewWorkflowName('');
    }
  };

  return (
    <SettingsContainer>
      <SettingHeader
        description="Configuration reserved for superusers and developers"
        title="Admin Settings"
      />
      <SettingsContent className="gap-6 self-start pb-8">
        <div className="rounded-xl bg-card p-6 shadow-verail">
          <h3 className="mb-4 font-medium text-lg">Process Settings</h3>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <Label htmlFor="process-name">Processus name</Label>
              <Input
                id="process-name"
                onBlur={() => void onNameBlur()}
                onChange={e => setProcessName(e.target.value)}
                placeholder="My beautiful processus"
                value={processName}
              />
              {emptyNameError && (
                <p className="text-destructive text-sm">
                  Process name can't be empty
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="process-icon">Avatar Image URL</Label>
              <div className="flex items-center gap-4">
                <Avatar>
                  <AvatarImage alt="Process avatar" src={process.icon} />
                  <AvatarFallback>?</AvatarFallback>
                </Avatar>
                <Input
                  className="flex-1"
                  id="process-icon"
                  onBlur={() => void onIconBlur()}
                  onChange={e => process.setIcon(e.target.value)}
                  placeholder="https://example.com/avatar.png"
                  value={process.icon}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="docker-image">Default Docker Image</Label>
              <Input
                id="docker-image"
                onBlur={() => void onImageBlur()}
                onChange={e => process.setImage(e.target.value)}
                placeholder="registry.sia.partners/stratumn/configuration/<repo-label>/<env>-stratumn-com"
                value={process.image}
              />
            </div>
          </div>
        </div>
        {/* `key` forces remount when the active process changes so TanStack
            Form's `defaultValues` (captured at mount) reflect the new
            process. Without this, switching processes saves Process A's
            form values to Process B. */}
        <WorkspaceSettingsCard key={process.id} processId={process.id} />

        <div className="rounded-xl bg-card p-6 shadow-verail">
          <h3 className="mb-1 font-medium text-lg">Workflow Settings</h3>
          <p className="mb-4 text-muted-foreground text-sm">
            Create and manage workflows for this process
          </p>
          <h4 className="mb-2 font-medium text-sm">Create Workflow</h4>
          <div className="flex items-center gap-4">
            <Input
              id="new-workflow-name"
              onChange={e => setNewWorkflowName(e.target.value)}
              placeholder="New Workflow"
              value={newWorkflowName}
            />
            <Button
              disabled={!newWorkflowName.trim() || creatingWorkflow}
              onClick={() => void onCreateWorkflow()}
              size="sm"
              type="button"
            >
              {creatingWorkflow && (
                <Loader2 className="mr-2 size-4 animate-spin" />
              )}
              Create
            </Button>
          </div>
          <WorkflowListCard process={process} />
        </div>
        <EnvVarsCard processId={process.id} />
        <div className="rounded-xl bg-card p-6 shadow-verail">
          <h3 className="mb-1 font-medium text-destructive text-lg">
            Danger Zone
          </h3>
          <p className="mb-4 text-muted-foreground text-sm">
            Permanently delete this project and all of its data
          </p>
          <Dialog
            onOpenChange={setIsDeleteDialogOpen}
            open={isDeleteDialogOpen}
          >
            <DialogTrigger asChild>
              <Button disabled={!canDelete} variant="destructive">
                Delete the project
              </Button>
            </DialogTrigger>
            <DeleteDialogContent
              isDraft={process.draft}
              nuke={deleteProcess}
              setOpen={setIsDeleteDialogOpen}
              stats={{
                actionCount: actionStore.data.size,
                transitionCount: transitionStore.data.size,
                workflowCount: workflowStore.data.size
              }}
            />
          </Dialog>
        </div>
      </SettingsContent>
    </SettingsContainer>
  );
};

export default observer(AdminSetting);
